import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import PrivateLessonPanelShell from './PrivateLessonPanelShell';
import {
  COLORS,
  TRAINER_PALETTE,
  EVENT_TYPE_META,
  SplitViewLayout,
  MonthGridContainer,
  MonthDowRow,
  MonthDowCell,
  MonthWeekRow,
  MonthDayCell,
  MonthCellHeader,
  MonthDayNumber,
  MonthTodayBadge,
  MonthCellCount,
  MonthEventList,
  MonthEventRow,
  MonthEventBar,
  MonthEventTime,
  MonthEventName,
  MonthMoreLabel,
  MonthMorePopover,
  MonthMorePopoverBody,
  DetailPanelHeader,
  DetailPanelTitle,
  DetailPanelCountPill,
  DetailPanelBody,
  DetailPanelFooter,
  DetailPanelFooterLink,
  DetailPanelAddButton,
  EventTypeBadge,
  PanelTrainerGroup,
  PanelTrainerGroupHeader,
  PanelTrainerDot,
  PanelTrainerName,
  PanelTrainerCount,
  PanelGroupItemRow,
  PanelItemTopLine,
  PanelConsultContent,
  PanelItemTime,
  PanelItemName,
  PanelItemCount,
  PanelRowJumpPill,
  WeekPanelLoadMoreHint,
  WeekPanelItemBar,
  MonthEventTrainer,
} from './PrivateLessonStyled';
import { getEventTypeKey, getEventCardKey } from './hooks/usePrivateLessonData';
import LessonHoverTooltip from './LessonHoverTooltip';
import { DAY_OF_WEEK_INDEX, endOfWeekBy, startOfWeekBy } from '../weekStart';
import useScheduleEventDrag from './hooks/useScheduleEventDrag';
import {
  getEventBarColor,
  getEventDisplayName,
  getGroupMemberCount,
  GROUP_LESSON_LABEL,
} from './lessonStatus';

const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];

// 셀 높이에서 표시 가능한 이벤트 행수 파생 (헤더 31px + '+N건 더' 15px 제외, 행 19px + gap 2px)
const MONTH_CELL_HEADER_HEIGHT = 31;
const MONTH_MORE_LABEL_HEIGHT = 15;
const MONTH_EVENT_ROW_HEIGHT = 21;
const getMaxVisibleEvents = cellHeight =>
  Math.max(1, Math.floor((cellHeight - MONTH_CELL_HEADER_HEIGHT - MONTH_MORE_LABEL_HEIGHT) / MONTH_EVENT_ROW_HEIGHT));

// '+n건 더' 확장 레이어 크기 규칙
const POPOVER_MAX_HEIGHT = 360;
const POPOVER_MIN_WIDTH = 220;
const POPOVER_BODY_PADDING = 8; // 목록 아래 여백(6) + 테두리(2)
const VIEWPORT_MARGIN = 8;

/**
 * 확장 레이어 위치. 칸 위치에 겹쳐 아래로 펼치고, 뷰포트 아래 공간이 모자라면 칸 아래 끝에 맞춰 위로 펼친다.
 * 위·아래 어느 쪽도 다 못 담으면 더 넓은 쪽에 맞추고 내부 스크롤에 맡긴다.
 */
const getPopoverLayout = (rect, eventCount) => {
  const wanted = Math.min(
    MONTH_CELL_HEADER_HEIGHT + eventCount * MONTH_EVENT_ROW_HEIGHT + POPOVER_BODY_PADDING,
    POPOVER_MAX_HEIGHT,
  );
  const width = Math.max(rect.width, POPOVER_MIN_WIDTH);
  const left = Math.max(VIEWPORT_MARGIN, Math.min(rect.left, window.innerWidth - width - VIEWPORT_MARGIN));
  const spaceBelow = window.innerHeight - rect.top - VIEWPORT_MARGIN;
  const spaceAbove = rect.bottom - VIEWPORT_MARGIN;

  if (wanted <= spaceBelow || spaceBelow >= spaceAbove) {
    return { top: rect.top, left, width, maxHeight: Math.min(wanted, spaceBelow) };
  }
  return { bottom: window.innerHeight - rect.bottom, left, width, maxHeight: Math.min(wanted, spaceAbove) };
};

// 요일 헤더 색
const getDowColor = day => (day === 0 ? COLORS.redSunday : day === 6 ? COLORS.blueSaturday : '#666');

// 날짜 숫자 색 (이번 달 평일/일/토, 전·다음 달)
const getDayNumberColor = (day, isOtherMonth) => {
  if (isOtherMonth) return '#c9c9c9';
  if (day === 0) return COLORS.redSunday;
  if (day === 6) return COLORS.blueSaturday;
  return '#404040';
};

/**
 * 개인 스케줄 월별 보기 (분할형)
 * - 좌: 월 그리드 (셀 클릭 = 선택일 변경)
 * - 우: 선택일 상세 패널 (임직원별 그룹)
 */
const PrivateLessonMonthView = ({
  currentDate,
  weekStartDay = 'SUNDAY', // 주 시작 요일 — 그리드 첫 열
  // 기간 조회 모드 — 있으면 그리드를 이 범위 기준으로 그리고, 범위 밖 날짜는 숫자만 남긴다
  rangeStart,
  rangeEnd,
  selectedDate,
  onSelectDate,
  events,
  staffList,
  selectedTrainerIds,
  cellHeight = 128,
  panelWidth,
  panelCollapsed,
  onTogglePanel,
  onPanelWidthChange,
  onEventClick,
  onEventContextMenu,
  onEventDrop,
  canDragEvent = true,
  onDayCellContextMenu,
  onGoToDay,
  onAddSchedule,
  onGroupJump,
  ticketAlertCount,
  ticketAlertCountUnit,
  typeFilter,
}) => {
  const maxVisibleEvents = getMaxVisibleEvents(cellHeight);
  // 그룹레슨 포함(일일 보기와 동일) + 임직원 필터 적용 후 날짜별 그룹핑 (시간순 정렬)
  const eventsByDate = useMemo(() => {
    const byDate = {};
    events.forEach(event => {
      if (selectedTrainerIds.length > 0 && !selectedTrainerIds.includes(event.trainerId)) return;
      const key = event.registrationStartDate;
      if (!byDate[key]) byDate[key] = [];
      byDate[key].push(event);
    });
    Object.values(byDate).forEach(list =>
      list.sort((a, b) => (a.startTime || '').localeCompare(b.startTime || '')),
    );
    return byDate;
  }, [events, selectedTrainerIds]);

  // 그리드 주 배열 — 기본은 당월, 기간 조회면 그 범위를 덮는 주 단위 (양끝 이월 일자 포함)
  const weeks = useMemo(() => {
    const start = startOfWeekBy(rangeStart || currentDate.clone().startOf('month'), weekStartDay);
    const end = endOfWeekBy(rangeEnd || currentDate.clone().endOf('month'), weekStartDay);
    const result = [];
    const cursor = start.clone();
    while (cursor.isSameOrBefore(end, 'day')) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(cursor.clone());
        cursor.add(1, 'day');
      }
      result.push(week);
    }
    return result;
  }, [currentDate, rangeStart, rangeEnd, weekStartDay]);

  const selectedEvents = eventsByDate[selectedDate.format('YYYY-MM-DD')] || [];

  // 선택일 이벤트를 임직원별 그룹으로 (통합 임직원 목록 순서 = 색 팔레트 순서)
  const trainerGroups = useMemo(() => {
    const byTrainer = new Map();
    selectedEvents.forEach(event => {
      if (!byTrainer.has(event.trainerId)) byTrainer.set(event.trainerId, []);
      byTrainer.get(event.trainerId).push(event);
    });

    const groups = [];
    staffList.forEach((staff, index) => {
      const items = byTrainer.get(staff.trainerId);
      if (items) {
        groups.push({
          trainerId: staff.trainerId,
          trainerName: staff.trainerName,
          color: TRAINER_PALETTE[index % TRAINER_PALETTE.length],
          items,
        });
        byTrainer.delete(staff.trainerId);
      }
    });
    // 통합 목록에 없는 담당자(퇴사자 등)의 이벤트도 뒤에 표시
    byTrainer.forEach((items, trainerId) => {
      groups.push({
        trainerId,
        trainerName: items[0]?.trainerName || '-',
        color: TRAINER_PALETTE[groups.length % TRAINER_PALETTE.length],
        items,
      });
    });
    return groups;
  }, [selectedEvents, staffList]);

  // 패널 목록 무한 스크롤 — 주간 패널과 동일 (항목 기준 20건씩, 강사 그룹핑은 유지)
  const [visibleCount, setVisibleCount] = useState(20);
  useEffect(() => {
    setVisibleCount(20);
  }, [selectedDate, selectedEvents.length]);

  const handlePanelScroll = e => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 80) {
      setVisibleCount(count => Math.min(count + 20, selectedEvents.length));
    }
  };

  // 패널 높이를 좌측 월 그리드에 맞춤 — 패널이 그리드보다 길어지면 내부 스크롤이 생기지 않아
  // 스크롤 기반 무한 로드가 발동하지 못하므로, 그리드 높이를 측정해 maxHeight로 제한한다
  const gridRef = useRef(null);
  const [gridHeight, setGridHeight] = useState(null);
  useEffect(() => {
    if (!gridRef.current) return undefined;
    const observer = new ResizeObserver(entries => {
      const height = entries[0]?.contentRect?.height;
      if (height) setGridHeight(Math.round(height));
    });
    observer.observe(gridRef.current);
    return () => observer.disconnect();
  }, []);

  // 내용이 패널을 다 못 채우면(스크롤 불가) 스크롤 없이도 다음 20건을 자동 로드
  const panelBodyRef = useRef(null);
  useEffect(() => {
    const body = panelBodyRef.current;
    if (!body) return;
    if (visibleCount < selectedEvents.length && body.scrollHeight <= body.clientHeight) {
      setVisibleCount(count => Math.min(count + 20, selectedEvents.length));
    }
  }, [visibleCount, selectedEvents.length, gridHeight]);

  // visibleCount 예산만큼 그룹 순서대로 항목을 잘라 표시
  const visibleTrainerGroups = useMemo(() => {
    let budget = visibleCount;
    const result = [];
    for (const group of trainerGroups) {
      if (budget <= 0) break;
      const items = group.items.slice(0, budget);
      budget -= items.length;
      result.push({ ...group, items, totalCount: group.items.length });
    }
    return result;
  }, [trainerGroups, visibleCount]);

  const today = moment();

  // '+n건 더' 확장 레이어 — { dateKey, rect(칸의 뷰포트 좌표), dragging }. dragging 이면 숨기기만 한다.
  // 끄는 행이 이 레이어 안에 있어서 DOM 에서 지우면 드래그가 끊긴다.
  const [popover, setPopover] = useState(null);
  const popoverRef = useRef(null);
  const [dropDateKey, setDropDateKey] = useState(null);
  const { dragRef, dragEvent, endDrag, getDragProps } = useScheduleEventDrag({
    enabled: canDragEvent,
    onStart: () => setTimeout(() => setPopover(prev => (prev ? { ...prev, dragging: true } : prev)), 0),
    onEnd: () => {
      setPopover(null);
      setDropDateKey(null);
    },
  });

  // 바깥 클릭·Esc·스크롤·리사이즈로 닫는다 (rect 가 뷰포트 좌표라 스크롤하면 어긋난다)
  useEffect(() => {
    if (!popover || popover.dragging) return undefined;
    const close = () => setPopover(null);
    const handleMouseDown = e => {
      if (!popoverRef.current?.contains(e.target)) close();
    };
    const handleKeyDown = e => {
      if (e.key === 'Escape') close();
    };
    const handleScroll = e => {
      if (!popoverRef.current?.contains(e.target)) close();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [popover]);

  const openMorePopover = (e, dateKey) => {
    e.stopPropagation();
    const rect = e.currentTarget.closest('[data-date]').getBoundingClientRect();
    setPopover({
      dateKey,
      rect: { left: rect.left, top: rect.top, bottom: rect.bottom, width: rect.width },
      dragging: false,
    });
  };

  // 칸 안·레이어 안 공용 일정 행 (클릭·우클릭·드래그 동일)
  const renderEventRow = (event, index, keyPrefix, afterClick) => (
    <MonthEventRow
      key={getEventCardKey(event, `${keyPrefix}-${index}`)}
      className="month-event-row"
      data-class-time-id={event.courseClassType === 'CONSULTATION' ? undefined : event.courseClassTimeId}
      data-consultation-id={event.courseClassType === 'CONSULTATION' ? event.consultationId : undefined}
      style={dragEvent === event ? { opacity: 0.4 } : undefined}
      {...getDragProps(event)}
      onClick={e => {
        afterClick?.();
        onEventClick(e, event, index);
      }}
      onContextMenu={e => onEventContextMenu?.(e, event)}>
      <MonthEventBar barColor={getEventBarColor(event, typeFilter)} />
      {event.trainerName && <MonthEventTrainer>{event.trainerName}</MonthEventTrainer>}
      <MonthEventTime>{event.startTime}</MonthEventTime>
      <MonthEventName>{getEventDisplayName(event)}</MonthEventName>
    </MonthEventRow>
  );

  const handleCellDrop = (e, day) => {
    e.preventDefault();
    const dropped = dragRef.current;
    endDrag();
    setDropDateKey(null);
    if (dropped) onEventDrop?.(dropped, day.clone());
  };

  const popoverEvents = popover ? eventsByDate[popover.dateKey] || [] : [];
  const popoverDay = popover ? moment(popover.dateKey, 'YYYY-MM-DD') : null;

  return (
    <SplitViewLayout>
      {/* 좌: 월 그리드 */}
      <MonthGridContainer ref={gridRef}>
        <MonthDowRow>
          {/* 요일 머리글 — 주 시작 요일부터 7개 */}
          {Array.from({ length: 7 }, (_, offset) => (DAY_OF_WEEK_INDEX[weekStartDay] + offset) % 7).map(dow => (
            <MonthDowCell key={dow} dayColor={getDowColor(dow)}>
              {DAY_NAMES[dow]}
            </MonthDowCell>
          ))}
        </MonthDowRow>

        {weeks.map((week, weekIdx) => (
          <MonthWeekRow key={weekIdx}>
            {week.map(day => {
              const dateKey = day.format('YYYY-MM-DD');
              // 표시 범위 밖(당월 이월 일자 / 기간 밖) — 날짜 숫자만 회색으로 남기고 이벤트는 그리지 않는다
              const isOutside = rangeStart
                ? !day.isBetween(rangeStart, rangeEnd, 'day', '[]')
                : !day.isSame(currentDate, 'month');
              const dayEvents = isOutside ? [] : eventsByDate[dateKey] || [];
              const isToday = day.isSame(today, 'day');
              const isSelected = day.isSame(selectedDate, 'day');
              const hiddenCount = dayEvents.length - maxVisibleEvents;

              return (
                <MonthDayCell
                  key={dateKey}
                  isToday={isToday}
                  isSelected={isSelected}
                  cellHeight={cellHeight}
                  data-date={dateKey}
                  sx={
                    dropDateKey === dateKey
                      ? { backgroundColor: 'rgba(74, 179, 188, 0.15)', boxShadow: 'inset 0 0 0 2px #4AB3BC' }
                      : undefined
                  }
                  onClick={() => onSelectDate(day.clone())}
                  onDragOver={e => {
                    if (!dragRef.current || isOutside) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    setDropDateKey(prev => (prev === dateKey ? prev : dateKey));
                  }}
                  onDragLeave={e => {
                    if (!e.currentTarget.contains(e.relatedTarget)) setDropDateKey(prev => (prev === dateKey ? null : prev));
                  }}
                  onDrop={e => {
                    if (isOutside) return;
                    handleCellDrop(e, day);
                  }}
                  onContextMenu={e => {
                    if (isOutside) return;
                    // 월간은 칸이 하루라 시간을 못 정한다 — 원본 시간·담당자를 그대로 두고 날짜만 옮긴다.
                    if (e.target.closest('.month-event-row')) return;
                    onDayCellContextMenu?.(e, day.clone());
                  }}>
                  <MonthCellHeader>
                    {isToday ? (
                      <MonthTodayBadge>{day.date()}</MonthTodayBadge>
                    ) : (
                      <MonthDayNumber numColor={getDayNumberColor(day.day(), isOutside)}>
                        {day.date()}
                      </MonthDayNumber>
                    )}
                    {dayEvents.length > 0 && <MonthCellCount>{dayEvents.length}건</MonthCellCount>}
                  </MonthCellHeader>

                  <MonthEventList>
                    {dayEvents.slice(0, maxVisibleEvents).map((event, index) =>
                      renderEventRow(event, index, dateKey),
                    )}
                    {hiddenCount > 0 && (
                      <MonthMoreLabel
                        role="button"
                        tabIndex={0}
                        onClick={e => openMorePopover(e, dateKey)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openMorePopover(e, dateKey);
                          }
                        }}>
                        +{hiddenCount}건 더
                      </MonthMoreLabel>
                    )}
                  </MonthEventList>
                </MonthDayCell>
              );
            })}
          </MonthWeekRow>
        ))}
      </MonthGridContainer>

      {/* '+n건 더' 확장 레이어 — 칸 overflow 에 안 잘리도록 body 로 뺀다 */}
      {/* 포털 객체는 Box(SplitViewLayout)의 children propType(node)에 맞지 않아 경고가 난다 — 프래그먼트로 감싼다 */}
      <>
          {popover &&
          createPortal(
            <MonthMorePopover
              ref={popoverRef}
              role="dialog"
              aria-label={`${popoverDay.format('M월 D일')} 일정 전체`}
              style={{
                ...getPopoverLayout(popover.rect, popoverEvents.length),
                ...(popover.dragging ? { visibility: 'hidden', pointerEvents: 'none' } : null),
              }}>
              <MonthCellHeader>
                <MonthDayNumber numColor={getDayNumberColor(popoverDay.day(), false)}>
                  {popoverDay.format('M월 D일')} ({DAY_NAMES[popoverDay.day()]})
                </MonthDayNumber>
                <MonthCellCount>{popoverEvents.length}건</MonthCellCount>
              </MonthCellHeader>
              <MonthMorePopoverBody>
                {popoverEvents.map((event, index) =>
                  renderEventRow(event, index, `${popover.dateKey}-more`, () => setPopover(null)),
                )}
              </MonthMorePopoverBody>
            </MonthMorePopover>,
            document.body,
          )}
      </>

      {/* 우: 선택일 상세 패널 (접기/가로폭 조절) */}
      <PrivateLessonPanelShell
        width={panelWidth || 440}
        collapsed={panelCollapsed}
        onToggleCollapse={onTogglePanel}
        onWidthChange={onPanelWidthChange}
        count={selectedEvents.length}
        maxHeight={gridHeight}>
        <DetailPanelAddButton onClick={onAddSchedule}>+ 일정 추가</DetailPanelAddButton>
        <DetailPanelHeader>
          <DetailPanelTitle>
            {selectedDate.format('M월 D일')} ({DAY_NAMES[selectedDate.day()]})
          </DetailPanelTitle>
          <DetailPanelCountPill>{selectedEvents.length}건</DetailPanelCountPill>
        </DetailPanelHeader>

        <DetailPanelBody ref={panelBodyRef} onScroll={handlePanelScroll}>
          {visibleTrainerGroups.map(group => (
            <PanelTrainerGroup key={group.trainerId}>
              <PanelTrainerGroupHeader>
                <PanelTrainerDot dotColor={group.color} />
                <PanelTrainerName>{group.trainerName} 강사</PanelTrainerName>
                <PanelTrainerCount>{group.totalCount}건</PanelTrainerCount>
              </PanelTrainerGroupHeader>

              {group.items.map((event, index) => {
                const typeMeta = EVENT_TYPE_META[getEventTypeKey(event.courseClassType)];
                const isGroup = event.courseClassType === 'GROUP_LESSON';
                const isConsult = event.courseClassType === 'CONSULTATION';
                const memberCount = getGroupMemberCount(event);
                // 기타는 내용 원문이 목록에 실려 온다 (상담은 200자 절단본)
                const consultContent = isConsult
                  ? event.consultContentPreview
                  : event.courseClassType === 'ETC'
                    ? event.memo
                    : '';
                return (
                  <LessonHoverTooltip
                    key={getEventCardKey(event, `panel-${index}`)}
                    event={event}
                    ticketAlertCount={ticketAlertCount}
                    ticketAlertCountUnit={ticketAlertCountUnit}
                    placement="left">
                    <PanelGroupItemRow onClick={e => (isGroup ? onGroupJump(event) : onEventClick(e, event, index))}>
                      <PanelItemTopLine>
                        <WeekPanelItemBar barColor={getEventBarColor(event, typeFilter)} />
                        <PanelItemTime>{event.startTime}</PanelItemTime>
                        <PanelItemName>
                          {memberCount ? GROUP_LESSON_LABEL : getEventDisplayName(event)}
                          {memberCount && <PanelItemCount>{memberCount}</PanelItemCount>}
                        </PanelItemName>
                        <EventTypeBadge badgeColor={typeMeta.color} badgeBg={typeMeta.badgeBg}>
                          {typeMeta.label}
                        </EventTypeBadge>
                      </PanelItemTopLine>
                      {consultContent && <PanelConsultContent>{consultContent}</PanelConsultContent>}
                      {isGroup && (
                        <PanelRowJumpPill
                          className="panel-jump-pill"
                          onClick={e => {
                            e.stopPropagation();
                            onGroupJump(event);
                          }}>
                          수업으로 이동 ›
                        </PanelRowJumpPill>
                      )}
                    </PanelGroupItemRow>
                  </LessonHoverTooltip>
                );
              })}
            </PanelTrainerGroup>
          ))}
          {visibleCount < selectedEvents.length && (
            <WeekPanelLoadMoreHint>스크롤하여 더 보기 · 남은 {selectedEvents.length - visibleCount}건</WeekPanelLoadMoreHint>
          )}
        </DetailPanelBody>

        <DetailPanelFooter>
          <DetailPanelFooterLink onClick={onGoToDay}>일(직원별) 보기로 이동 →</DetailPanelFooterLink>
        </DetailPanelFooter>
      </PrivateLessonPanelShell>
    </SplitViewLayout>
  );
};

export default PrivateLessonMonthView;
